import { useMemo } from "react";
import { CheckCircle2, Copy, MapPin, Pencil, Plus, Truck, Wrench } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { MAQUINARIO_STATUS_LABELS, type MaquinarioDto } from "@/services/locacoes";
import { MAQUINARIO_STATUS_THEME } from "./statusTheme";
import {
  locacoesSectionTitleClass,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
} from "./locacoesUtils";
import { cn } from "@/utils/cn";
import { LocacoesStatCard } from "./LocacoesStatCard";
import { LocacoesSubToolbar } from "./LocacoesSubToolbar";

interface Props {
  maquinarios: MaquinarioDto[];
  onNovaMaquina: () => void;
  onEditMaquina: (m: MaquinarioDto) => void;
  onDuplicateMaquina: (m: MaquinarioDto) => void;
}

export function TabCadastroMaquinas({
  maquinarios,
  onNovaMaquina,
  onEditMaquina,
  onDuplicateMaquina,
}: Props) {
  const kpi = useMemo(
    () => ({
      total: maquinarios.length,
      disponiveis: maquinarios.filter((m) => m.status === "disponivel").length,
      locadas: maquinarios.filter((m) => m.status === "locado").length,
      manutencao: maquinarios.filter((m) => m.status === "manutencao").length,
    }),
    [maquinarios],
  );

  return (
    <div className={locacoesTabContentClass}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <LocacoesStatCard title="Total" value={kpi.total} icon={<Truck className="h-5 w-5" />} accentClass="border-l-4 border-l-blue-400" />
        <LocacoesStatCard title="Disponíveis" value={kpi.disponiveis} icon={<CheckCircle2 className="h-5 w-5" />} accentClass="border-l-4 border-l-emerald-400" />
        <LocacoesStatCard title="Locadas" value={kpi.locadas} icon={<MapPin className="h-5 w-5" />} accentClass="border-l-4 border-l-sky-400" />
        <LocacoesStatCard title="Em manutenção" value={kpi.manutencao} icon={<Wrench className="h-5 w-5" />} accentClass="border-l-4 border-l-amber-400" />
      </div>

      <LocacoesSubToolbar
        title="Cadastro"
        end={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={onNovaMaquina}>
            Nova máquina
          </Button>
        }
      >
        <div className={locacoesSectionTitleClass}>
          <Truck className="h-4 w-4 text-brand-600" />
          <span>Máquinas cadastradas ({maquinarios.length})</span>
        </div>
      </LocacoesSubToolbar>

      <div className={locacoesTableWrapClass}>
        <table className="min-w-full text-sm">
          <thead className={locacoesTableHeadClass}>
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Código</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Placa</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {maquinarios.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  Nenhuma máquina cadastrada.
                </td>
              </tr>
            ) : (
              maquinarios.map((m) => {
                const theme = MAQUINARIO_STATUS_THEME[m.status];
                return (
                  <tr key={m.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 font-medium text-slate-800">{m.nome}</td>
                    <td className="px-4 py-3 text-slate-600">{m.codigoInterno || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{m.tipo || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{m.placa || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                          theme.badge,
                        )}
                      >
                        <span className={cn("h-1.5 w-1.5 rounded-full", theme.dot)} />
                        {MAQUINARIO_STATUS_LABELS[m.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditMaquina(m)}
                          leftIcon={<Pencil className="h-3.5 w-3.5" />}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDuplicateMaquina(m)}
                          leftIcon={<Copy className="h-3.5 w-3.5" />}
                          title="Duplicar"
                        >
                          Duplicar
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
